import _ from 'lodash';
import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { getFirstObject } from '../../common/convert';
import { SettingsViewerContext } from '../../components/viewers/FullSettingsViewer';
import { gEnums } from '../../enums/globalEnums';
import { itemHandlers, itemInitialState, itemReducer } from '../reducers/ItemReducer';
import { DataContext } from './DataContext';
import { FilterContext } from './FilterContext';
import { PageAreaContext } from './PageAreaContext';
import { PageAreaParentContext } from './PageAreaParentContext';
import { ParentContext } from './ParentContext';
import { useAppUserAccess } from './useHelpers/useAppUserAccess';
import { useItemInit } from './useHelpers/useItemInit';

/** 
 @provides itemAction_handlers
 @provides isCombinedDatea
 @provides uiFilter_handlers
 @provides uiFilter_state
 @provides uiItemsStyle
 @provides updates
 @provides view
 @provides viewItem
 @provides viewListData 
 */

const _showMemos = false

export const UiItemContext = createContext();

const UiItemProvider = (props) => {

  // ParentContext
  const parentContext = useContext(ParentContext);
  const { states, fns, settings, fn_cls } = parentContext ?? {}
  const { app_state, appSettings_state, paps_state, currentSettings_state } = states
  const { appUser_fns, page_fns } = fns ?? {}
  const { aps_global, aps_viewItems, aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});
  const { gvviSettings } = currentSettings_state ?? {}
  const { homeSettings } = settings ?? {}
  const { global: homeSettings_global } = homeSettings ?? {}
  const { logging } = homeSettings_global ?? {}
  const { dataOptions, appDataSource } = aps_global ?? {}
  const { propOverrideOn } = appSettings_state ?? {}
  const { useModifyRoutes } = dataOptions ?? {}
  const { mainDb } = app_state ?? {}

  const appUserAccess = useAppUserAccess()

  // pageAreaParentContext
  const pageAreaParentContext = useContext(PageAreaParentContext)
  const { states: parent_states_p, handlers: parent_handlers_p } = pageAreaParentContext ?? {}
  const { framework_state } = parent_states_p ?? {}
  const { framework_handlers } = parent_handlers_p ?? {}

  // papsContext  
  const { pathViews, view, viewKey, _isDataModify, _dataModifyType } = paps_state ?? {}

  const viewItem_g = aps_viewItems ? aps_viewItems[view] : null

  // frameworkContext 
  const { desktopMode, tempSettings } = framework_state ?? {}

  // settingsViewerContext
  const settingsViewerContext = useContext(SettingsViewerContext)
  const { settingsViewer_state } = settingsViewerContext ?? {}
  const { viewItem_preview } = settingsViewer_state ?? {}

  // pageContext 
  const { appAccess, display: display_global } = aps_global ?? {}
  const { appAccessType } = appAccess ?? {}

  // pageAreaContext
  const pageAreaContext = useContext(PageAreaContext)
  const { pageArea_state } = pageAreaContext ?? {}
  const { pageItemsShown } = pageArea_state ?? {}

  // dataContext
  const dataContext = useContext(DataContext);
  const { data_state } = dataContext ?? {}
  const { uivi, viewListData, alphabetValue, excludedData } = data_state ?? {}

  const _tempSettings_vi = tempSettings && tempSettings[uivi]
  const { display: display_temp } = _tempSettings_vi ?? {}

  // filterContext
  const filterContext = useContext(FilterContext);
  const { uiFilter_state } = filterContext ?? {}
  const {
    alphaValue,
    filterSidebarOpen,
    filterStatusType,
    filterValues,
    groupBys,
    groupDataProps,
    searchValue,
  } = uiFilter_state ?? {}

  let { selectedTab } = uiFilter_state ?? {}

  // Now for the viewItem  
  let viewItem = pageItemsShown ? pageItemsShown[uivi] : {}
  if (viewItem_preview) { viewItem = viewItem_preview }

  let isPublicSite = false

  switch (appAccessType) {
    case gEnums.siteAuthorizationTypes.public:
    case gEnums.siteAuthorizationTypes.passcode:
      isPublicSite = true
      break;
    default:
    // nothing
  }

  const { display, desktopDisplay } = viewItem ?? {}

  let _display = desktopMode && desktopDisplay ? desktopDisplay : display
  if (display_temp) { _display = display_temp }

  const { item: _dataItem } = viewListData ? getFirstObject(viewListData) : {}

  const itemInits = useItemInit()

  if (groupDataProps && groupDataProps.defaultTab && !selectedTab) { selectedTab = groupDataProps.defaultTab }

  /**
   * This is inside useMemo
   * @param {string} viKey 
   * @returns 
   */

  const initState = useMemo(() => {

    _showMemos && console.log('useMemo > uiItemContext', uivi, pathViews, viewItem)

    const viewItem_key = viewItem ? viewItem.key : null

    return {
      ...itemInits,
      _display,
      appDataSource,
      appUser_fns, // for validationfv
      appUserAccess,
      excludedData,
      itemData: _dataItem,
      logging,
      modifyActionType: useModifyRoutes && _isDataModify && _dataModifyType ? _dataModifyType : null,
      page_fns,
      paps_state, // view, pathViews 
      pathViews,
      propOverrideOn,
      tabIndex: props.tabIndex,
      view,
      viewItem_g,
      viewItem_key,
      viewItem,
      viewKey,
      viewListData,
      fn_cls
    }
  }, [
    alphabetValue,
    aps_appUserSettings,
    filterSidebarOpen,
    filterStatusType,
    gvviSettings,
    itemInits,
    searchValue,
    mainDb,
    propOverrideOn,
    tempSettings,
    uivi,
    viewItem,
    viewListData,
  ]);

  const [item_state, itemDispatch] = useReducer(itemReducer, itemInitialState({}));
  const { overridesChanged } = item_state ?? {}

  const item_handlers = useMemo(
    () => itemHandlers(itemDispatch),
    [itemDispatch]
  );

  useEffect(() => {
    if (initState) {
      // console.group('************ init state', uivi)
      // console.log('initState', initState)
      // console.groupEnd()
      item_handlers.handleInit_item(initState)
    }
    // eslint-disable-next-line
  }, [uivi]);

  useEffect(() => {
    item_handlers.handleGet_validationAccess({ alphaValue, filterValues })
    // eslint-disable-next-line
  }, [alphaValue, filterValues && filterValues.length]);

  useEffect(() => {
    framework_handlers.handleChanged_override(overridesChanged)
    // eslint-disable-next-line
  }, [overridesChanged]);

  useEffect(() => {
    item_handlers.handleAmmend_updateFilterOpts({ alphaValue, filterValues, groupBys, filterStatusType, searchValue })
    // eslint-disable-next-line
  }, [alphaValue, groupBys, searchValue, filterValues && filterValues.length]);

  return <UiItemContext.Provider value={{ item_state, item_handlers }}>
    {props.children}
  </UiItemContext.Provider>

};

export default UiItemProvider 